// BIZ-CLIENT-HEALTH (true-business-gaps §1 Customers "Client health / NPS"): a client space's
// health in the web app. On the space's overview, a card: the score in its band with the week's
// trend, the five signals (label, bar, the facts behind it, (i) each), NPS, the account owner
// seat (hover pencil → a modal picking the seat, saved as the space's `settings.account_seat_id`),
// the open client issues and the recent surveys. "Send survey" (hover icon) asks for NPS or CSAT,
// the contact's name and an optional email, then shows the link to copy ("Mailed to x" when an
// email was given). A score of null is "Not enough data", never a made-up number. In the Work
// sidebar, `useClientHealth` feeds a coloured dot beside each client space (one cached query).
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  LensBandDot,
  NativeSelect,
  NativeSelectOption,
  Progress,
  QuickAddDialog,
  Skeleton,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownRightIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CopyIcon,
  PencilIcon,
  SendIcon,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { ago } from "./doc-kinds";
import { useSeatPeople } from "./model";
import { seatText } from "./playbook";
import { workKeys } from "./work-queries";

export type ClientHealth = OperationOutput<"clients.health">["data"][number];
type HealthDetail = OperationOutput<"clients.healthGet">;
type Space = OperationOutput<"spaces.list">["data"][number];

export const clientHealthKeys = {
  all: (co: string) => [...workKeys.all(co), "client-health"] as const,
  one: (co: string, id: string) => [...workKeys.all(co), "client-health", id] as const,
};

export const clientsHealthQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: clientHealthKeys.all(co),
    queryFn: () => api.client.clients.health({ params: { co } }),
    staleTime: 60_000,
  });

export const clientHealthQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: clientHealthKeys.one(co, id),
    queryFn: () => api.client.clients.healthGet({ params: { co, id } }),
    // Answers arrive from outside the app (the survey link): read afresh on every visit.
    refetchOnMount: "always",
  });

/** Client space id → its health (the sidebar's dots); empty while loading or without clients. */
export function useClientHealth(
  api: Api,
  co: string,
  enabled: boolean,
): ReadonlyMap<string, ClientHealth> {
  const q = useQuery({ ...clientsHealthQuery(api, co), enabled });
  const rows = q.data?.data;
  return useMemo(() => new Map((rows ?? []).map((h) => [h.space_id, h] as const)), [rows]);
}

const BAND_WORD: Record<ClientHealth["band"], string> = {
  green: "Healthy",
  amber: "Watch",
  red: "At risk",
  none: "Not enough data",
};

/** The sidebar's dot: the band's colour, "Health 72" on hover. */
export function HealthDot({ health }: { health: ClientHealth }) {
  const label = health.score === null ? "Health: not enough data" : `Health ${health.score}`;
  return (
    <LensBandDot
      band={health.band}
      aria-label={label}
      title={label}
      data-slot="client-health-dot"
      className="mr-1"
    />
  );
}

const SIGNAL_INFO: Record<HealthDetail["signals"][number]["key"], string> = {
  delays: "Tasks on this client's boards due in the last 30 days, and how many were late.",
  rework: "Finished work moved back to not done in the last 30 days.",
  issues: "Open issues about this client; each one takes 25 points off.",
  response: "Median time to the next reply in the client's conversations, last 30 days.",
  sentiment: "NPS and CSAT answers in 90 days: promoters 100, passives 50, detractors 0.",
};

const tone = (score: number) => (score >= 75 ? "default" : score >= 50 ? "warning" : "danger");

function Trend({ trend }: { trend: number | null }) {
  if (trend === null) return null;
  const Icon = trend > 0 ? ArrowUpRightIcon : trend < 0 ? ArrowDownRightIcon : ArrowRightIcon;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-xs text-muted-foreground tabular-nums"
      title="Change over 7 days"
      data-slot="client-health-trend"
    >
      <Icon aria-hidden="true" className="size-3.5" />
      {trend > 0 ? `+${trend}` : trend}
    </span>
  );
}

function Heading({ children, info }: { children: string; info: string }) {
  return (
    <h3 className="flex items-center gap-1 text-[11px] leading-4 font-medium tracking-wide text-faint uppercase">
      {children}
      <InfoTip label={`About ${children.toLowerCase()}`} className="size-4">
        {info}
      </InfoTip>
    </h3>
  );
}

/** The health card on a client space's overview. */
export function ClientHealthCard({
  api,
  company,
  space,
}: {
  api: Api;
  company: CompanyAccess;
  space: Space;
}) {
  const q = useQuery(clientHealthQuery(api, company.id, space.id));
  const [survey, setSurvey] = useState(false);
  const [owner, setOwner] = useState(false);
  if (q.isPending)
    return <Skeleton className="mb-4 h-40" aria-label="Loading the client's health" />;
  if (q.isError || !q.data) return null;
  const h = q.data;
  const canEdit = !space.archived_at;
  return (
    <section
      aria-label="Client health"
      data-slot="client-health"
      className="group/health mb-4 flex flex-col gap-4 rounded-lg border border-border bg-card p-4"
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="flex items-center gap-1 text-sm font-semibold">
          Client health
          <InfoTip label="About client health">
            The weighted mean of the signals that have data, 0–100. Green from 75, amber from 50.
          </InfoTip>
        </h2>
        {h.score === null ? (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <LensBandDot band="none" />
            Not enough data
          </span>
        ) : (
          <span className="flex items-center gap-2" data-slot="client-health-score">
            <span className="font-heading text-2xl font-semibold tabular-nums">{h.score}</span>
            <LensBandDot band={h.band} />
            <span className="text-sm text-muted-foreground">{BAND_WORD[h.band]}</span>
            <Trend trend={h.trend} />
          </span>
        )}
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto opacity-0 group-hover/health:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
            onClick={() => setSurvey(true)}
          >
            <SendIcon aria-hidden="true" />
            Send survey
          </Button>
        ) : null}
      </header>
      <div className="grid gap-x-8 gap-y-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <ul aria-label="Signals" className="m-0 flex list-none flex-col gap-2.5 p-0">
          {h.signals.map((s) => (
            <li key={s.key} data-signal={s.key} className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center gap-1 text-sm">
                <span className="font-medium">{s.label}</span>
                <InfoTip label={`About ${s.label.toLowerCase()}`} className="size-4">
                  {SIGNAL_INFO[s.key]}
                </InfoTip>
                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                  {s.score === null ? "—" : s.score}
                </span>
              </div>
              {s.score === null ? (
                <div className="h-1.5 w-full rounded-full bg-muted" aria-hidden="true" />
              ) : (
                <Progress
                  value={s.score}
                  tone={tone(s.score)}
                  aria-label={`${s.label} ${s.score}`}
                />
              )}
              <span className="truncate text-xs text-muted-foreground">{s.detail}</span>
            </li>
          ))}
        </ul>
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <Heading info="NPS over the NPS answers of the last 90 days: % promoters (9–10) minus % detractors (0–6).">
                NPS
              </Heading>
              <span className="text-lg font-semibold tabular-nums" data-slot="client-nps">
                {h.nps === null ? "—" : h.nps}
              </span>
            </div>
            <div className="group/owner flex min-w-0 flex-col gap-1">
              <Heading info="The seat that owns this client: it gets the issues its health raises.">
                Account owner
              </Heading>
              <span className="flex min-w-0 items-center gap-1">
                <AccountOwner api={api} company={company} seatId={h.account_seat?.seat_id} />
                {canEdit ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Change the account owner"
                    title="Change the account owner"
                    className="opacity-0 group-hover/owner:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                    onClick={() => setOwner(true)}
                  >
                    <PencilIcon aria-hidden="true" />
                  </Button>
                ) : null}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Heading info="Issues raised about this client (a detractor's answer raises one).">
              Open issues
            </Heading>
            {h.open_issues.length ? (
              <ul aria-label="Open issues" className="m-0 flex list-none flex-col gap-1 p-0">
                {h.open_issues.map((i) => (
                  <li key={i.id} className="flex min-w-0 items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate" title={i.title}>
                      {i.title}
                    </span>
                    <span className="text-xs text-faint">{ago(i.created_at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Heading info="NPS and CSAT surveys sent about this client; each link is answered once.">
              Surveys
            </Heading>
            {h.surveys.length ? (
              <ul aria-label="Surveys" className="m-0 flex list-none flex-col gap-1 p-0">
                {h.surveys.slice(0, 5).map((s) => (
                  <li key={s.id} className="flex min-w-0 items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{s.recipient_name}</span>
                    <Badge variant="outline">{s.kind === "nps" ? "NPS" : "CSAT"}</Badge>
                    <span className="w-14 text-right tabular-nums" data-slot="survey-score">
                      {s.score === null ? (
                        <span className="text-xs text-faint">Waiting</span>
                      ) : (
                        `${s.score}/${s.kind === "nps" ? 10 : 5}`
                      )}
                    </span>
                    <span className="w-16 text-right text-xs text-faint">
                      {ago(s.answered_at ?? s.sent_at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None sent</p>
            )}
          </div>
        </div>
      </div>
      {survey ? (
        <SendSurveyDialog
          api={api}
          company={company}
          space={space}
          onClose={() => setSurvey(false)}
        />
      ) : null}
      {owner ? (
        <AccountOwnerDialog
          api={api}
          company={company}
          space={space}
          current={h.account_seat?.seat_id ?? null}
          onClose={() => setOwner(false)}
        />
      ) : null}
    </section>
  );
}

/** The account owner seat as its holder (a DE with its badge) and title; "None" without one. */
function AccountOwner({
  api,
  company,
  seatId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string | undefined;
}) {
  const people = useSeatPeople(api, company.id);
  if (!seatId) return <span className="text-sm text-muted-foreground">None</span>;
  const p = people.of(seatId);
  return (
    <span className="flex min-w-0 items-center gap-1 text-sm" data-slot="account-owner">
      {p.de ? <WorkerBadge kind="de" size="sm" /> : null}
      <span className="truncate font-medium">{p.open ? p.seat : workerName(p.name)}</span>
      {p.open ? null : <span className="truncate text-xs text-faint">{p.seat}</span>}
    </span>
  );
}

function AccountOwnerDialog({
  api,
  company,
  space,
  current,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  space: Space;
  current: string | null;
  onClose: () => void;
}) {
  const co = company.id;
  const queryClient = useQueryClient();
  const people = useSeatPeople(api, co);
  const [seat, setSeat] = useState(current ?? "");
  const id = useId();
  const save = useMutation({
    mutationFn: () =>
      api.client.spaces.update({
        params: { co, id: space.id },
        body: { settings: { ...space.settings, account_seat_id: seat || null } },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientHealthKeys.all(co) }),
        queryClient.invalidateQueries({ queryKey: workKeys.spaces(co) }),
      ]);
      toast.add({ title: "Account owner saved", type: "success" });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Account owner"
      info="The seat that owns this client: a person's or a DE's. It gets the issues the client's health raises."
      description={space.name}
      submitLabel="Save"
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="account-owner-dialog"
      error={
        save.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(save.error, "The account owner was not saved.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={id}>Seat</FieldLabel>
        <NativeSelect id={id} value={seat} onChange={(e) => setSeat(e.target.value)}>
          <NativeSelectOption value="">None</NativeSelectOption>
          {people.all
            .filter((s) => !s.closed)
            .map((s) => (
              <NativeSelectOption key={s.seatId} value={s.seatId}>
                {seatText(s)}
              </NativeSelectOption>
            ))}
        </NativeSelect>
      </Field>
    </QuickAddDialog>
  );
}

function SendSurveyDialog({
  api,
  company,
  space,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  space: Space;
  onClose: () => void;
}) {
  const co = company.id;
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<"nps" | "csat">("nps");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const nameId = useId();
  const emailId = useId();
  const send = useMutation({
    mutationFn: () =>
      api.client.clients.surveyCreate({
        params: { co, id: space.id },
        body: {
          kind,
          recipient_name: name.trim(),
          ...(email.trim() ? { recipient_email: email.trim() } : {}),
        },
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: clientHealthKeys.one(co, space.id) }),
        queryClient.invalidateQueries({ queryKey: clientHealthKeys.all(co), exact: true }),
      ]),
  });
  if (send.data) {
    const url = `${window.location.origin}${send.data.link}`;
    const mailed = send.data.survey.recipient_email;
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent data-slot="survey-sent">
          <DialogHeader>
            <DialogTitle>Survey ready</DialogTitle>
            <DialogDescription>
              {mailed
                ? `Mailed to ${mailed}`
                : `Send this link to ${send.data.survey.recipient_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={url} aria-label="Survey link" className="min-w-0 flex-1" />
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(url)
                  .then(() => toast.add({ title: "Link copied" }))
                  .catch(() => toast.add({ title: "Copy the link by hand", type: "error" }));
              }}
            >
              <CopyIcon aria-hidden="true" />
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <QuickAddDialog
      title="Send survey"
      info="NPS asks how likely they are to recommend you (0–10); CSAT how satisfied they are (1–5). The link works once, for 30 days."
      description={space.name}
      submitLabel="Send"
      canSubmit={name.trim().length > 0}
      pending={send.isPending}
      onSubmit={() => send.mutate()}
      onClose={onClose}
      slot="send-survey-dialog"
      error={
        send.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(send.error, "The survey was not sent.")}
          </p>
        ) : null
      }
    >
      <div role="radiogroup" aria-label="Kind" className="flex gap-1.5">
        {(["nps", "csat"] as const).map((k) => (
          <Button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            variant={kind === k ? "default" : "secondary"}
            size="sm"
            onClick={() => setKind(k)}
          >
            {k === "nps" ? "NPS" : "CSAT"}
          </Button>
        ))}
      </div>
      <Field>
        <FieldLabel htmlFor={nameId}>Name</FieldLabel>
        <Input
          id={nameId}
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={emailId}>Email (optional)</FieldLabel>
        <Input
          id={emailId}
          type="email"
          maxLength={320}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
    </QuickAddDialog>
  );
}
